import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Car, Home as HomeIcon, Heart, Briefcase, Shield, Clock, 
  ChevronRight, Phone, ArrowRight, CheckCircle, FileText,
  Building2, Users, Truck, Umbrella, Bike, Anchor, Zap, Scale
} from 'lucide-react';

const servicesData = [
  {
    id: 'auto',
    icon: Car,
    title: 'Assurance Automobile',
    subtitle: 'Tous véhicules, tous profils',
    description: 'De la formule économique au tous risques, nous trouvons la couverture adaptée à votre véhicule et votre budget.',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-50',
    features: [
      'Formules au tiers, intermédiaire et tous risques',
      'Jeunes conducteurs et résiliés acceptés',
      'Assistance 24h/24 France et Europe',
      'Véhicule de remplacement en option',
      'Bonus protégé disponible'
    ],
    link: '/quote?type=auto'
  },
  {
    id: 'moto',
    icon: Bike,
    title: 'Assurance 2 Roues',
    subtitle: 'Motos, scooters, quads',
    description: 'Protection complète pour tous vos deux-roues : motos, scooters, quads et véhicules de collection.',
    color: 'from-orange-500 to-orange-600',
    bgColor: 'bg-orange-50',
    features: [
      'Toutes cylindrées et motorisations',
      'Équipements pilote et passager couverts',
      'Vol et incendie inclus',
      'Assistance rapatriement',
      'Tarifs jeunes permis compétitifs'
    ],
    link: '/quote?type=moto'
  },
  {
    id: 'habitation',
    icon: HomeIcon,
    title: 'Assurance Habitation',
    subtitle: 'Propriétaire ou locataire',
    description: 'Protégez votre logement et vos biens contre tous les risques du quotidien.',
    color: 'from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-50',
    features: [
      'Incendie, dégât des eaux, vol',
      'Responsabilité civile incluse',
      'Protection juridique en option',
      'Objets de valeur couverts',
      'Garantie rééquipement à neuf'
    ],
    link: '/quote?type=habitation'
  },
  {
    id: 'sante',
    icon: Heart,
    title: 'Santé & Prévoyance',
    subtitle: 'Votre santé, notre priorité',
    description: 'Complémentaire santé et contrats de prévoyance pour vous et votre famille.',
    color: 'from-rose-500 to-rose-600',
    bgColor: 'bg-rose-50',
    features: [
      'Remboursements hospitalisation',
      'Optique et dentaire renforcés',
      'Médecines douces prises en charge',
      'Tiers payant généralisé',
      'Prévoyance décès et invalidité'
    ],
    link: '/quote?type=sante'
  },
  {
    id: 'bateau',
    icon: Anchor,
    title: 'Bateau & Jet Ski',
    subtitle: 'Plaisance et sports nautiques',
    description: 'Assurance dédiée aux embarcations de plaisance, jet skis et engins nautiques motorisés.',
    color: 'from-cyan-500 to-cyan-600',
    bgColor: 'bg-cyan-50',
    features: [
      'Bateaux moteur et voiliers',
      'Jet ski et scooters des mers',
      'Responsabilité civile navigation',
      'Vol, incendie, dommages',
      'Assistance en mer 24h/24'
    ],
    link: '/quote?type=bateau'
  },
  {
    id: 'trottinette',
    icon: Zap,
    title: 'Trottinettes & NVEI',
    subtitle: 'Mobilité électrique urbaine',
    description: 'Protection obligatoire pour vos nouveaux véhicules électriques individuels : trottinettes, gyroroues, hoverboards.',
    color: 'from-lime-500 to-lime-600',
    bgColor: 'bg-lime-50',
    features: [
      'Responsabilité civile obligatoire',
      'Vol et dommages matériels',
      'Protection conducteur',
      'Tous types de NVEI couverts',
      'Tarifs accessibles'
    ],
    link: '/quote?type=trottinette'
  },
  {
    id: 'juridique',
    icon: Scale,
    title: 'Protection Juridique',
    subtitle: 'Vos droits défendus',
    description: 'Couverture juridique complète pour vous défendre et faire valoir vos droits dans tous les domaines de la vie.',
    color: 'from-indigo-500 to-indigo-600',
    bgColor: 'bg-indigo-50',
    features: [
      'Litiges consommation et travail',
      'Conflits de voisinage',
      'Défense pénale et recours',
      'Accompagnement juridique',
      'Prise en charge frais d\'avocat'
    ],
    link: '/quote?type=juridique'
  },
  {
    id: 'pro',
    icon: Briefcase,
    title: 'Assurance Professionnels',
    subtitle: 'Artisans, commerçants, PME',
    description: 'Solutions complètes pour protéger votre activité et votre responsabilité.',
    color: 'from-amber-500 to-amber-600',
    bgColor: 'bg-amber-50',
    features: [
      'RC Professionnelle',
      'Multirisque locaux et matériel',
      'Protection juridique métier',
      'Perte d\'exploitation',
      'Cyber-risques'
    ],
    link: '/quote?type=pro'
  },
  {
    id: 'decennale',
    icon: Building2,
    title: 'Garantie Décennale',
    subtitle: 'Constructeurs et artisans du BTP',
    description: 'Assurance obligatoire pour tous les professionnels du bâtiment.',
    color: 'from-violet-500 to-violet-600',
    bgColor: 'bg-violet-50',
    features: [
      'Tous corps de métiers du BTP',
      'Gros œuvre et second œuvre',
      'Attestation immédiate',
      'Tarifs négociés',
      'Accompagnement sinistre'
    ],
    link: '/quote?type=decennale'
  },
  {
    id: 'temporaire',
    icon: Clock,
    title: 'Assurance Temporaire',
    subtitle: 'De 1 à 90 jours — 24h/24',
    description: 'Besoin d\'une assurance auto pour quelques jours ? Souscription immédiate en ligne.',
    color: 'from-cyan-500 to-cyan-600',
    bgColor: 'bg-cyan-50',
    features: [
      'Souscription 100% en ligne',
      'Carte verte par email instantanée',
      'Import, export, convoyage',
      'Essai avant achat',
      'Tous véhicules acceptés'
    ],
    link: '/assurance-temporaire',
    highlighted: true
  },
  {
    id: 'flotte',
    icon: Truck,
    title: 'Flotte Automobile',
    subtitle: 'À partir de 3 véhicules',
    description: 'Contrat unique pour tous vos véhicules professionnels avec gestion simplifiée.',
    color: 'from-slate-500 to-slate-600',
    bgColor: 'bg-slate-50',
    features: [
      'Gestion centralisée',
      'Tarifs dégressifs',
      'Véhicules de remplacement',
      'Reporting sinistres',
      'Interlocuteur dédié'
    ],
    link: '/quote?type=flotte'
  },
  {
    id: 'carte-grise',
    icon: FileText,
    title: 'Carte Grise',
    subtitle: 'Service agréé SIV',
    description: 'Démarches d\'immatriculation rapides et simplifiées, sans vous déplacer.',
    color: 'from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    features: [
      'Immatriculation véhicule neuf',
      'Changement de titulaire',
      'Duplicata et corrections',
      'Changement d\'adresse',
      'Envoi à domicile'
    ],
    link: '/carte-grise'
  }
];

export const Services = () => {
  return (
    <>
      <Helmet>
        <title>Nos Assurances | Cabinet CAMBC Conseils Moissac 82</title>
        <meta name="description" content="Découvrez nos solutions d'assurance : auto, habitation, santé, professionnels, décennale, temporaire. Courtier à Moissac (82)." />
        <meta name="keywords" content="assurance 82, assurance Moissac, courtier assurance, auto, habitation, santé, pro, décennale" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/services" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Nos Expertises
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                Solutions d'assurance
                <span className="block text-[#C5A059]">sur mesure</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Particuliers et professionnels, nous analysons vos besoins pour vous proposer 
                les garanties adaptées au meilleur tarif auprès des plus grands assureurs.
              </p>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {servicesData.map((service) => (
                <Card 
                  key={service.id}
                  className={`bg-white border-0 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden group ${
                    service.highlighted ? 'ring-2 ring-[#C5A059] ring-offset-2' : ''
                  }`}
                >
                  <CardContent className="p-0">
                    <div className="p-8">
                      {/* Header */}
                      <div className="flex items-start gap-5 mb-6">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <service.icon size={28} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-xl font-bold text-[#0F2137]">{service.title}</h3>
                            {service.highlighted && (
                              <span className="px-2 py-0.5 bg-[#C5A059] text-white text-xs font-bold rounded-full">
                                24/7
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#C5A059] font-medium">{service.subtitle}</p>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-slate-600 mb-6 leading-relaxed">
                        {service.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <CheckCircle size={18} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <Link to={service.link}>
                        <Button className={`w-full ${
                          service.highlighted 
                            ? 'bg-[#C5A059] hover:bg-[#B08A45]' 
                            : 'bg-[#0F2137] hover:bg-[#1E3A5F]'
                        } text-white font-semibold py-6 rounded-xl transition-all duration-300 group-hover:shadow-lg`}>
                          {service.highlighted ? 'Souscrire maintenant' : 'Demander un devis'}
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F5EFE6] rounded-full text-[#C5A059] text-sm font-medium mb-6">
              <Shield size={16} />
              <span>Conseil personnalisé</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F2137] mb-6">
              Besoin d'un conseil ?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Notre équipe est à votre disposition pour analyser vos besoins et vous proposer 
              la solution d'assurance la plus adaptée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0582819120">
                <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08A45] text-white font-semibold px-8 py-6 rounded-xl">
                  <Phone size={18} className="mr-2" />
                  05 82 81 91 20
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-2 border-[#0F2137] text-[#0F2137] hover:bg-[#0F2137] hover:text-white px-8 py-6 rounded-xl">
                  Nous contacter
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
